/**
 * Per-hostname configuration overrides.
 *
 * One build serves more than one hostname. This module resolves the
 * per-hostname profile from a single JSON map, `VITE_HOST_OVERRIDES`.
 */

/** The fields a hostname can override. */
export interface HostProfile {
  apiBaseUrl: string;
  defaultReturnUrl: string;
}

/** A map from hostname to its override fields. A missing field keeps the default. */
export type HostOverrides = Record<string, Partial<HostProfile>>;

/**
 * Parses the `VITE_HOST_OVERRIDES` value.
 *
 * Undefined or empty text gives an empty map. Malformed text throws.
 */
export function parseHostOverrides(raw: string | undefined): HostOverrides {
  if (raw === undefined || raw === "") {
    return {};
  }

  return JSON.parse(raw) as HostOverrides;
}

/**
 * Resolves the profile for one hostname.
 *
 * The result starts from the defaults. The entry whose key equals the
 * hostname, compared case-insensitively, overlays its fields on top.
 */
export function resolveHostProfile(
  hostname: string,
  overrides: HostOverrides,
  defaults: HostProfile
): HostProfile {
  const matchKey = Object.keys(overrides).find(
    (key) => key.toLowerCase() === hostname.toLowerCase()
  );

  if (matchKey === undefined) {
    return defaults;
  }

  return { ...defaults, ...overrides[matchKey] };
}
